// Deal Pipeline spec §9.2 — DocumentTypeRef catalog.
//
// A migration seeded the canonical DocumentTypeRef table (spec §9.2 — 23
// rows). The static catalog below mirrors it. A later change added the
// read endpoint (/api/deal-lookups/document-types); the
// `useDocumentTypes` hook is the canonical client surface and falls
// back to the static catalog for instant loads / offline / pre-fetch.
// New code should import the hook; the static catalog remains for
// label lookup (`documentTypeLabel`) and as the fallback fixture.

import { useQuery } from "@tanstack/react-query";
import type { PreApprovalRequestParty } from "@/types/deals";
import { api } from "./client";

export interface DocumentTypeOption {
  code: string;
  displayName: string;
  defaultParty: PreApprovalRequestParty;
  defaultRequired: boolean;
  defaultDueDays: number;
  sortOrder: number;
  // Admin-authored escalation guidance for spawned
  // document requests. Only present on the live catalog (not the static
  // fallback); the Document Requests surface falls back to a computed string
  // when this is blank/absent.
  defaultEscalationPath?: string | null;
}

export const DOCUMENT_TYPES: DocumentTypeOption[] = [
  { code: "PFS", displayName: "Personal Financial Statement (signed)", defaultParty: "Borrower", defaultRequired: true, defaultDueDays: 7, sortOrder: 10 },
  { code: "PERSONAL_TAX_3YR", displayName: "Last 3 years personal tax returns", defaultParty: "Borrower", defaultRequired: true, defaultDueDays: 7, sortOrder: 20 },
  { code: "PERSONAL_BANK_2MO", displayName: "2 most recent personal bank statements", defaultParty: "Borrower", defaultRequired: true, defaultDueDays: 7, sortOrder: 30 },
  { code: "RESUME", displayName: "Resume / business background summary", defaultParty: "Borrower", defaultRequired: true, defaultDueDays: 7, sortOrder: 40 },
  { code: "CREDIT_AUTH", displayName: "Credit authorization form", defaultParty: "Borrower", defaultRequired: true, defaultDueDays: 7, sortOrder: 50 },
  { code: "BUSINESS_PLAN", displayName: "Business plan / acquisition strategy", defaultParty: "Borrower", defaultRequired: true, defaultDueDays: 14, sortOrder: 60 },
  { code: "EQUITY_SOURCE", displayName: "Equity injection source documentation", defaultParty: "Borrower", defaultRequired: false, defaultDueDays: 7, sortOrder: 70 },
  { code: "ACCRED_INV_Q", displayName: "Accredited investor questionnaire", defaultParty: "Seller", defaultRequired: true, defaultDueDays: 7, sortOrder: 80 },
  { code: "BIZ_TAX_3YR", displayName: "Last 3 years business tax returns", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 90 },
  { code: "BIZ_FIN_3YR", displayName: "Last 3 years business financials", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 100 },
  { code: "BIZ_FIN_YTD", displayName: "YTD business financials", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 110 },
  { code: "BIZ_BANK_3MO", displayName: "Business bank statements (last 3 months)", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 120 },
  { code: "CUSTOMER_CONC", displayName: "Customer concentration report", defaultParty: "Business", defaultRequired: true, defaultDueDays: 14, sortOrder: 130 },
  { code: "VENDOR_LIST", displayName: "Vendor / supplier list", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 140 },
  { code: "LEASE", displayName: "Lease / property documents", defaultParty: "Business", defaultRequired: false, defaultDueDays: 7, sortOrder: 150 },
  { code: "OP_AGREEMENT", displayName: "Operating agreement / bylaws", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 160 },
  { code: "DEBT_SCHEDULE", displayName: "Existing debt schedule", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 170 },
  { code: "INSURANCE_CERTS", displayName: "Insurance certificates (current)", defaultParty: "Business", defaultRequired: true, defaultDueDays: 7, sortOrder: 180 },
  { code: "LOI_FINAL", displayName: "Letter of Intent (final)", defaultParty: "Broker", defaultRequired: true, defaultDueDays: 7, sortOrder: 190 },
  { code: "PA_DRAFT", displayName: "Draft Purchase Agreement", defaultParty: "Broker", defaultRequired: true, defaultDueDays: 14, sortOrder: 200 },
  { code: "QOE", displayName: "Quality of Earnings (if available)", defaultParty: "Broker", defaultRequired: false, defaultDueDays: 14, sortOrder: 210 },
  { code: "BROKER_CERT", displayName: "Broker certification & disclosure form", defaultParty: "Broker", defaultRequired: true, defaultDueDays: 7, sortOrder: 220 },
  { code: "BROKER_FEE", displayName: "Broker fee authorization", defaultParty: "Broker", defaultRequired: false, defaultDueDays: 7, sortOrder: 230 },
];

const DOCUMENT_TYPE_BY_CODE = new Map<string, DocumentTypeOption>(
  DOCUMENT_TYPES.map((d) => [d.code, d]),
);

export function documentTypeLabel(code: string): string {
  return DOCUMENT_TYPE_BY_CODE.get(code)?.displayName ?? code;
}

export function documentTypeFor(code: string): DocumentTypeOption | undefined {
  return DOCUMENT_TYPE_BY_CODE.get(code);
}

interface DocumentTypeRefDto {
  code: string;
  displayName: string;
  description?: string | null;
  defaultParty: string;
  defaultRequired: boolean;
  defaultDueDays: number;
  defaultEscalationPath?: string | null;
  isActive: boolean;
  sortOrder: number;
}

const DOCUMENT_TYPES_QUERY_KEY = ["dealLookups", "documentTypes"] as const;

/**
 * Deal Pipeline spec §4.4 — fetches the live DocumentTypeRef catalog
 * from /api/deal-lookups/document-types so admin edits propagate
 * without a frontend deploy. Falls back to the static catalog above
 * during the first load and on transient errors so UI never renders
 * empty.
 */
export function useDocumentTypes(): {
  data: DocumentTypeOption[];
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: DOCUMENT_TYPES_QUERY_KEY,
    queryFn: () =>
      api.get<DocumentTypeRefDto[]>("deal-lookups/document-types"),
    // Catalog rarely changes; cache aggressively.
    staleTime: 10 * 60_000,
    gcTime: 60 * 60_000,
  });

  const mapped = (q.data ?? [])
    .filter((row) => row.isActive)
    .map<DocumentTypeOption>((row) => ({
      code: row.code,
      displayName: row.displayName,
      defaultParty: row.defaultParty as PreApprovalRequestParty,
      defaultRequired: row.defaultRequired,
      defaultDueDays: row.defaultDueDays,
      sortOrder: row.sortOrder,
      defaultEscalationPath: row.defaultEscalationPath,
    }));

  // Fallback to static catalog when the request hasn't returned
  // (initial mount) or has errored. Keeps the UI clickable.
  const data = mapped.length > 0 ? mapped : DOCUMENT_TYPES;

  return {
    data,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
